/**
 * Merkt sich Dokumentlisten für die Dauer der Sitzung, damit ein schon besuchter
 * Ordner sofort dasteht, während die aktuelle Fassung im Hintergrund kommt
 * (User 01.11.2026).
 *
 * Der Server bleibt die Quelle der Wahrheit: Ein Eintrag wird nur angezeigt und
 * immer sofort neu geladen. Geschrieben wird nie aus dem Cache.
 *
 * Bewusst nur im Arbeitsspeicher und je Benutzer — das sind Kundendaten. Ohne
 * Benutzer-Id gibt es keinen Cache; beim Abmelden wird er geleert.
 */
export type ListenStand<T> = { rows: T[]; total: number; hasMore: boolean };

export type ListenSchluessel = {
  userId: string;
  contactId: number;
  /** Alles Weitere, was die Liste bestimmt: Modus, Pfad, Papierkorb. */
  ansicht: readonly unknown[];
};

/** Älteres zeigen wir nicht mehr — es sähe aus wie der aktuelle Stand. */
export const CACHE_DAUER_MS = 10 * 60 * 1000;
export const CACHE_MAX_EINTRAEGE = 40;

type Eintrag = { stand: ListenStand<unknown>; zeit: number; contactId: number };

const eintraege = new Map<string, Eintrag>();
const alsText = (k: ListenSchluessel) => JSON.stringify([k.userId, k.contactId, k.ansicht]);

export const dokumentListenCache = {
  lesen<T>(k: ListenSchluessel): ListenStand<T> | null {
    if (!k.userId) return null;
    const text = alsText(k);
    const eintrag = eintraege.get(text);
    if (!eintrag) return null;
    if (Date.now() - eintrag.zeit > CACHE_DAUER_MS) {
      eintraege.delete(text);
      return null;
    }
    return eintrag.stand as ListenStand<T>;
  },

  schreiben<T>(k: ListenSchluessel, stand: ListenStand<T>): void {
    if (!k.userId) return;
    const text = alsText(k);
    // Neu einsortieren: Die Map bleibt so nach dem letzten Schreiben geordnet.
    eintraege.delete(text);
    eintraege.set(text, { stand, zeit: Date.now(), contactId: k.contactId });
    while (eintraege.size > CACHE_MAX_EINTRAEGE) {
      eintraege.delete(eintraege.keys().next().value as string);
    }
  },

  verwerfen(k: ListenSchluessel): void {
    eintraege.delete(alsText(k));
  },

  /** Nach einer Änderung: Auch andere Ordner desselben Kontakts können betroffen sein. */
  kontaktVerwerfen(contactId: number): void {
    for (const [text, eintrag] of eintraege) {
      if (eintrag.contactId === contactId) eintraege.delete(text);
    }
  },

  leeren(): void {
    eintraege.clear();
  },
};
